import React, { createContext, useState, useEffect, useContext } from 'react';

const API_URL = 
process.env.NODE_ENV === 'development'
? process.env.REACT_APP_API_URL
: process.env.REACT_APP_SIDEKICK_API_URL;

const AuthContext = createContext({
  user: undefined,
  token: undefined,
  isLoggedIn: false,
  login: async () => {},
  logout: () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false); // Track login status
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          const response = await fetch(`${API_URL}/users`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setIsLoggedIn(true); // Set as logged in
          } else {
            localStorage.removeItem('authToken');
            setIsLoggedIn(false); // User is not logged in
          }
        } else {
          setIsLoggedIn(false); // No token, user is not logged in
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsLoggedIn(false); // In case of error, consider user as logged out
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) throw new Error('Login failed');

      const { token, user: userData } = await response.json();
      localStorage.setItem('authToken', token);
      setUser(userData); // Set the user state after login
      setIsLoggedIn(true); // Explicitly set as logged in
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    setIsLoggedIn(false); // Explicitly set logged out
  };

  return (
    <AuthContext.Provider value={{ user, isLoggedIn, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);